import HotTable from '@handsontable/react-wrapper';
import { Alert, Button } from '@mui/material';
import dayjs from 'dayjs';
import { ColumnSettings } from 'handsontable/settings';
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import {
  HedgePlanBudgetTriggerModel,
  HedgePlanTimeTriggerModel,
  HedgePlanType,
  HedgePlanValueTriggerModel,
  UpdateHedgePlanBudgetTriggerModel,
  UpdateHedgePlanBudgetTriggersRequest,
  UpdateHedgePlanTimeTriggerModel,
  UpdateHedgePlanTimeTriggersRequest,
  UpdateHedgePlanValueTriggerModel,
  UpdateHedgePlanValueTriggersRequest,
} from '../../../../Generated/Commodity-Management-Api';
import { StoneXRow } from '../../../StoneX';
import { StoneXButton } from '../../../StoneXMui';
import './HedgePlanGridValidators';
import { isHedgePlanSourceRowEmpty } from './HedgePlanGridValidators';
import HedgePlanSeasonTabLayout from './HedgePlanSeasonTabLayout';
import { SeasonPeriod } from './HedgePlanTerms';

interface HedgePlanTriggersTabProps {
  onClose: () => void;
  onSaveFailed: (message: string) => void;
  onSaved: (
    period: SeasonPeriod,
    valueTriggers: HedgePlanValueTriggerModel[],
    budgetTriggers: HedgePlanBudgetTriggerModel[],
    timeTriggers: HedgePlanTimeTriggerModel[],
  ) => void;
  hedgePlanId: number;
  period: SeasonPeriod;
  planType: HedgePlanType;
  valueTriggers: HedgePlanValueTriggerModel[];
  budgetTriggers: HedgePlanBudgetTriggerModel[];
  timeTriggers: HedgePlanTimeTriggerModel[];
  enableValueTriggers: boolean;
  enableBudgetTriggers: boolean;
  enableTimeTriggers: boolean;
}

type TriggerKind = 'value' | 'budget' | 'time';

const TRIGGER_COLUMN_WIDTH = 150;
const TARGET_COLUMN_WIDTH = 130;
const NOTE_COLUMN_WIDTH = 220;
const DISABLED_COLUMN_WIDTH = 80;
const DELETE_COLUMN_WIDTH = 40;

const HEADER_HEIGHT = 48;
const ROW_HEIGHT = 40;
// Handsontable sizes a dropdown/date editor's popup to fit within its OWN grid's workspace
// height, not the modal or page - with too few rows that workspace is too short for the popup
// to expand into. minRows pads the grid with empty rows up to this floor so height="auto" always
// gives Handsontable enough workspace, without us computing pixel heights by hand.
const MIN_VISIBLE_ROWS = 5;

interface TriggerRow {
  hedgePlanValueTriggerId?: number | null;
  hedgePlanBudgetTriggerId?: number | null;
  hedgePlanTimeTriggerId?: number | null;
  decilePercentile?: number | null;
  price?: number | null;
  triggerDate?: string | null;
  // Rolling plans only: what a time trigger is saved as.
  daysFromSeasonStart?: number | null;
  targetHedgePercent: number | null;
  disabled: boolean;
  note: string | null;
  deleteAction?: null;
}

function deleteRowRenderer(instance: any, tableCell: HTMLTableCellElement, row: number) {
  tableCell.replaceChildren();
  if (row === instance.countRows() - 1) {
    return;
  }

  const deleteButton = document.createElement('span');
  deleteButton.innerText = 'X';
  deleteButton.title = 'Delete row';
  deleteButton.setAttribute('role', 'button');
  deleteButton.setAttribute('aria-label', 'Delete row');
  deleteButton.style.cursor = 'pointer';
  deleteButton.style.color = 'var(--font-color)';
  deleteButton.onclick = () => instance.alter('remove_row', row);
  tableCell.appendChild(deleteButton);
}

const deleteColumn: ColumnSettings = {
  data: 'deleteAction',
  width: DELETE_COLUMN_WIDTH,
  readOnly: true,
  className: 'htCenter htMiddle',
  renderer: deleteRowRenderer,
};

const valueColumns: ColumnSettings[] = [
  { data: 'hedgePlanValueTriggerId', readOnly: true, className: 'htHidden' },
  { data: 'decilePercentile', width: TRIGGER_COLUMN_WIDTH, type: 'dropdown', source: ['10', '20', '30', '40', '50', '60', '70', '80', '90'], className: 'numeric', validator: 'decilePercentileValidator' },
  { data: 'targetHedgePercent', width: TARGET_COLUMN_WIDTH, type: 'numeric', className: 'numeric', validator: 'percentValidator' },
  { data: 'note', width: NOTE_COLUMN_WIDTH, type: 'text' },
  { data: 'disabled', width: DISABLED_COLUMN_WIDTH, type: 'checkbox' },
  deleteColumn,
];

const budgetColumns: ColumnSettings[] = [
  { data: 'hedgePlanBudgetTriggerId', readOnly: true, className: 'htHidden' },
  { data: 'price', width: TRIGGER_COLUMN_WIDTH, type: 'numeric', numericFormat: { pattern: '0,0.0000' }, className: 'numeric', validator: 'priceValidator' },
  { data: 'targetHedgePercent', width: TARGET_COLUMN_WIDTH, type: 'numeric', className: 'numeric', validator: 'percentValidator' },
  { data: 'note', width: NOTE_COLUMN_WIDTH, type: 'text' },
  { data: 'disabled', width: DISABLED_COLUMN_WIDTH, type: 'checkbox' },
  deleteColumn,
];

const timeColumns: ColumnSettings[] = [
  { data: 'hedgePlanTimeTriggerId', readOnly: true, className: 'htHidden' },
  { data: 'triggerDate', width: TRIGGER_COLUMN_WIDTH, type: 'date', dateFormat: 'YYYY-MM-DD', correctFormat: true, validator: 'dateValidator' },
  { data: 'targetHedgePercent', width: TARGET_COLUMN_WIDTH, type: 'numeric', className: 'numeric', validator: 'percentValidator' },
  { data: 'note', width: NOTE_COLUMN_WIDTH, type: 'text' },
  { data: 'disabled', width: DISABLED_COLUMN_WIDTH, type: 'checkbox' },
  deleteColumn,
];

interface TableSettings {
  columns: ColumnSettings[];
  headers: string[];
  dataSchema: TriggerRow;
}

function addDays(date: string, days: number): string {
  return dayjs.utc(date.substring(0, 10)).add(days, 'day').format('YYYY-MM-DD');
}

function daysBetween(from: string, to: string): number {
  return dayjs.utc(to.substring(0, 10)).diff(dayjs.utc(from.substring(0, 10)), 'day');
}

// A rolling plan's time triggers are entered as days from the season start, with this season's date shown beside them.
function rollingTimeSettings(seasonStartDate: string): TableSettings {
  function seasonDateRenderer(_instance: any, tableCell: HTMLTableCellElement, _row: number, _column: number, _prop: any, value: any) {
    const days = value === null || value === undefined || value === '' ? NaN : Number(value);
    tableCell.innerText = Number.isInteger(days) ? addDays(seasonStartDate, days) : '';
    tableCell.style.color = 'var(--gray4)';
  }

  return {
    columns: [
      { data: 'hedgePlanTimeTriggerId', readOnly: true, className: 'htHidden' },
      { data: 'daysFromSeasonStart', width: TRIGGER_COLUMN_WIDTH, type: 'numeric', className: 'numeric', validator: 'dayCountValidator' },
      { data: 'daysFromSeasonStart', width: TRIGGER_COLUMN_WIDTH, readOnly: true, renderer: seasonDateRenderer },
      ...timeColumns.slice(2),
    ],
    headers: ['Id', 'Days From Season Start', 'Date This Season', 'Target Hedge %', 'Note', 'Disabled', ''],
    dataSchema: { hedgePlanTimeTriggerId: null, daysFromSeasonStart: null, targetHedgePercent: null, disabled: false, note: null, deleteAction: null },
  };
}

const tableSettings: Record<TriggerKind, TableSettings> = {
  value: {
    columns: valueColumns,
    headers: ['Id', 'Decile Percentile', 'Target Hedge %', 'Note', 'Disabled', ''],
    dataSchema: { hedgePlanValueTriggerId: null, decilePercentile: null, targetHedgePercent: null, disabled: false, note: null, deleteAction: null },
  },
  budget: {
    columns: budgetColumns,
    headers: ['Id', 'Price', 'Target Hedge %', 'Note', 'Disabled', ''],
    dataSchema: { hedgePlanBudgetTriggerId: null, price: null, targetHedgePercent: null, disabled: false, note: null, deleteAction: null },
  },
  time: {
    columns: timeColumns,
    headers: ['Id', 'Trigger Date', 'Target Hedge %', 'Note', 'Disabled', ''],
    dataSchema: { hedgePlanTimeTriggerId: null, triggerDate: null, targetHedgePercent: null, disabled: false, note: null, deleteAction: null },
  },
};

function displayDate(value: string | null | undefined): string | null {
  return value ? value.substring(0, 10) : null;
}

function TriggerTable(props: {
  kind: TriggerKind;
  settings: TableSettings;
  rows: TriggerRow[];
  tableKey: string;
  hotRef: MutableRefObject<any>;
}) {
  const settings = props.settings;
  return (
    <div>
      <h3 style={{ marginBottom: 8 }}>{`${props.kind[0].toUpperCase()}${props.kind.substring(1)} Triggers`}</h3>
      <HotTable
        key={props.tableKey}
        ref={props.hotRef}
        data={props.rows}
        dataSchema={settings.dataSchema}
        colHeaders={settings.headers}
        columns={settings.columns}
        stretchH="all"
        width="100%"
        height="auto"
        rowHeights={ROW_HEIGHT}
        columnHeaderHeight={HEADER_HEIGHT}
        autoWrapRow
        autoWrapCol
        minRows={MIN_VISIBLE_ROWS}
        minSpareRows={1}
        isEmptyRow={(row) => isHedgePlanSourceRowEmpty(props.rows[row])}
        contextMenu={['row_above', 'row_below', 'remove_row']}
        hiddenColumns={{ columns: [0], indicators: false, copyPasteEnabled: false }}
        licenseKey="29ae0-f35ad-79c41-2472d-2c70d"
      />
    </div>
  );
}

function validateCells(hotInstance: any): Promise<boolean> {
  return new Promise((resolve) => hotInstance.validateCells((valid: boolean) => resolve(valid)));
}

export default function HedgePlanTriggersTab(props: HedgePlanTriggersTabProps) {
  const [modalSessionKey, setModalSessionKey] = useState(0);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const valueHotRef = useRef<any>(null);
  const budgetHotRef = useRef<any>(null);
  const timeHotRef = useRef<any>(null);

  useEffect(() => {
    setModalSessionKey((current) => current + 1);
    setValidationError(undefined);
  }, [props.period.key]);

  const selectedPeriod = props.period;
  const seasonStartDate = props.planType === HedgePlanType.Rolling ? selectedPeriod.startDate : undefined;
  const timeSettings = useMemo(() => (seasonStartDate ? rollingTimeSettings(seasonStartDate) : tableSettings.time), [seasonStartDate]);
  const rows = useMemo(() => {
    const matchesPeriod = (trigger: { year: number; term: string }) => trigger.year === selectedPeriod.year && trigger.term === selectedPeriod.term;
    return {
      value: props.valueTriggers.filter(matchesPeriod).map((trigger) => ({
        hedgePlanValueTriggerId: trigger.hedgePlanValueTriggerId,
        decilePercentile: trigger.decilePercentile,
        targetHedgePercent: trigger.targetHedgePercent,
        disabled: trigger.disabled,
        note: trigger.note ?? null,
      })),
      budget: props.budgetTriggers.filter(matchesPeriod).map((trigger) => ({
        hedgePlanBudgetTriggerId: trigger.hedgePlanBudgetTriggerId,
        price: trigger.price,
        targetHedgePercent: trigger.targetHedgePercent,
        disabled: trigger.disabled,
        note: trigger.note ?? null,
      })),
      time: props.timeTriggers.filter(matchesPeriod).map((trigger) => ({
        hedgePlanTimeTriggerId: trigger.hedgePlanTimeTriggerId,
        triggerDate: displayDate(trigger.triggerDate),
        daysFromSeasonStart: seasonStartDate ? daysBetween(seasonStartDate, trigger.triggerDate) : undefined,
        targetHedgePercent: trigger.targetHedgePercent,
        disabled: trigger.disabled,
        note: trigger.note ?? null,
      })),
    };
  }, [selectedPeriod, seasonStartDate, props.valueTriggers, props.budgetTriggers, props.timeTriggers, modalSessionKey]);

  function close() {
    if (!saving) {
      props.onClose();
    }
  }

  async function save() {
    if (!selectedPeriod) {
      return;
    }
    const hotInstances = [
      props.enableValueTriggers ? valueHotRef.current?.hotInstance : undefined,
      props.enableBudgetTriggers ? budgetHotRef.current?.hotInstance : undefined,
      props.enableTimeTriggers ? timeHotRef.current?.hotInstance : undefined,
    ].filter((hotInstance) => hotInstance !== undefined);
    if (hotInstances.some((hotInstance) => !hotInstance)) {
      return;
    }
    const validTables = await Promise.all(hotInstances.map(validateCells));
    if (validTables.some((valid) => !valid)) {
      setValidationError('Some rows are incomplete or invalid. Fix the highlighted cells, or clear the row entirely.');
      return;
    }

    const valueRows = props.enableValueTriggers ? valueHotRef.current.hotInstance.getSourceData() as TriggerRow[] : [];
    const budgetRows = props.enableBudgetTriggers ? budgetHotRef.current.hotInstance.getSourceData() as TriggerRow[] : [];
    const timeRows = props.enableTimeTriggers ? timeHotRef.current.hotInstance.getSourceData() as TriggerRow[] : [];

    const valueTriggers: UpdateHedgePlanValueTriggerModel[] = valueRows
      .filter((row) => row.decilePercentile !== null && row.decilePercentile !== undefined && row.targetHedgePercent !== null && row.targetHedgePercent !== undefined)
      .map((row) => ({
        hedgePlanValueTriggerId: row.hedgePlanValueTriggerId,
        decilePercentile: Number(row.decilePercentile),
        targetHedgePercent: Number(row.targetHedgePercent),
        disabled: !!row.disabled,
        note: row.note ?? null,
      }));
    const budgetTriggers: UpdateHedgePlanBudgetTriggerModel[] = budgetRows
      .filter((row) => row.price !== null && row.price !== undefined && row.targetHedgePercent !== null && row.targetHedgePercent !== undefined)
      .map((row) => ({
        hedgePlanBudgetTriggerId: row.hedgePlanBudgetTriggerId,
        price: Number(row.price),
        targetHedgePercent: Number(row.targetHedgePercent),
        disabled: !!row.disabled,
        note: row.note ?? null,
      }));
    const timeTriggerDate = (row: TriggerRow) => (seasonStartDate
      ? (row.daysFromSeasonStart === null || row.daysFromSeasonStart === undefined ? null : addDays(seasonStartDate, Number(row.daysFromSeasonStart)))
      : row.triggerDate);
    const timeTriggers: UpdateHedgePlanTimeTriggerModel[] = timeRows
      .filter((row) => !!timeTriggerDate(row) && row.targetHedgePercent !== null && row.targetHedgePercent !== undefined)
      .map((row) => ({
        hedgePlanTimeTriggerId: row.hedgePlanTimeTriggerId,
        triggerDate: timeTriggerDate(row) as string,
        targetHedgePercent: Number(row.targetHedgePercent),
        disabled: !!row.disabled,
        note: row.note ?? null,
      }));

    const valueRequest: UpdateHedgePlanValueTriggersRequest = { hedgePlanId: props.hedgePlanId, year: selectedPeriod.year, term: selectedPeriod.term, triggers: valueTriggers };
    const budgetRequest: UpdateHedgePlanBudgetTriggersRequest = { hedgePlanId: props.hedgePlanId, year: selectedPeriod.year, term: selectedPeriod.term, triggers: budgetTriggers };
    const timeRequest: UpdateHedgePlanTimeTriggersRequest = { hedgePlanId: props.hedgePlanId, year: selectedPeriod.year, term: selectedPeriod.term, triggers: timeTriggers };

    setSaving(true);
    setValidationError(undefined);
    try {
      const valueResponse = props.enableValueTriggers
        ? await CommodityManagementApi.updateHedgePlanValueTriggers(undefined, String(props.hedgePlanId), String(selectedPeriod.year), valueRequest)
        : undefined;
      const budgetResponse = props.enableBudgetTriggers
        ? await CommodityManagementApi.updateHedgePlanBudgetTriggers(undefined, String(props.hedgePlanId), String(selectedPeriod.year), budgetRequest)
        : undefined;
      const timeResponse = props.enableTimeTriggers
        ? await CommodityManagementApi.updateHedgePlanTimeTriggers(undefined, String(props.hedgePlanId), String(selectedPeriod.year), timeRequest)
        : undefined;
      props.onSaved(
        selectedPeriod,
        valueResponse?.data.valueTriggers ?? props.valueTriggers.filter((trigger) => trigger.year === selectedPeriod.year && trigger.term === selectedPeriod.term),
        budgetResponse?.data.budgetTriggers ?? props.budgetTriggers.filter((trigger) => trigger.year === selectedPeriod.year && trigger.term === selectedPeriod.term),
        timeResponse?.data.timeTriggers ?? props.timeTriggers.filter((trigger) => trigger.year === selectedPeriod.year && trigger.term === selectedPeriod.term),
      );
      props.onClose();
    } catch (error: any) {
      const message = error?.response?.data?.detail ?? error?.response?.data?.message ?? 'The triggers could not be saved.';
      setValidationError(message);
      props.onSaveFailed(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <HedgePlanSeasonTabLayout
      footer={(
        <StoneXRow justify="end">
          <Button variant="outlined" onClick={close} disabled={saving}>Cancel</Button>
          <StoneXButton onClick={save} isWorking={saving}>Save</StoneXButton>
        </StoneXRow>
      )}
    >
      <StoneXRow align="start">
        <div style={{ display: 'grid', gap: 20, minWidth: 0, width: '100%' }}>
          {props.enableValueTriggers && <TriggerTable kind="value" settings={tableSettings.value} rows={rows.value} tableKey={`value-${selectedPeriod.key}-${modalSessionKey}`} hotRef={valueHotRef} />}
          {props.enableBudgetTriggers && <TriggerTable kind="budget" settings={tableSettings.budget} rows={rows.budget} tableKey={`budget-${selectedPeriod.key}-${modalSessionKey}`} hotRef={budgetHotRef} />}
          {props.enableTimeTriggers && <TriggerTable kind="time" settings={timeSettings} rows={rows.time} tableKey={`time-${selectedPeriod.key}-${modalSessionKey}`} hotRef={timeHotRef} />}
        </div>
      </StoneXRow>

      {validationError && (
        <StoneXRow>
          <Alert severity="error" onClose={() => setValidationError(undefined)}>{validationError}</Alert>
        </StoneXRow>
      )}

    </HedgePlanSeasonTabLayout>
  );
}